import type { CSSProperties } from 'react';

import { getImageSizeMaxWidth, readExplicitImageSize } from '@/lib/blocks/image-size';
import { clampColumnSpan } from '@/lib/blocks/tree';
import { readBoolean, readPositiveNumber, readString } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';

const COLUMN_SPAN_CLASSES: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

const COLUMN_SPAN_MAX_WIDTH_CLASSES: Record<number, string> = {
  1: 'md:max-w-[8.333333%]',
  2: 'md:max-w-[16.666667%]',
  3: 'md:max-w-[25%]',
  4: 'md:max-w-[33.333333%]',
  5: 'md:max-w-[41.666667%]',
  6: 'md:max-w-[50%]',
  7: 'md:max-w-[58.333333%]',
  8: 'md:max-w-[66.666667%]',
  9: 'md:max-w-[75%]',
  10: 'md:max-w-[83.333333%]',
  11: 'md:max-w-[91.666667%]',
  12: 'md:max-w-[100%]',
};

export function getColumnSpanClassName(columnSpan: number): string {
  const normalized = clampColumnSpan(columnSpan);
  return COLUMN_SPAN_CLASSES[normalized] || COLUMN_SPAN_CLASSES[12];
}

export function getColumnSpanMaxWidthClassName(columnSpan: number): string {
  const normalized = clampColumnSpan(columnSpan);
  return COLUMN_SPAN_MAX_WIDTH_CLASSES[normalized] || COLUMN_SPAN_MAX_WIDTH_CLASSES[12];
}

// Imported blocks carry the width their media had on the old site, in pixels,
// which is used as a starting point rather than a fixed size.
export function getBlockDisplayWidthStyle(
  block: Pick<PageBlock, 'blockType' | 'props'>,
): CSSProperties | undefined {
  if (block.blockType !== 'image' && block.blockType !== 'video') return undefined;

  if (block.blockType === 'image') {
    if (readBoolean(block.props?.fullBleed) === true) return undefined;

    const imageSize = readExplicitImageSize(block.props?.imageSize);
    if (imageSize !== undefined) {
      const maxWidth = getImageSizeMaxWidth(imageSize);
      const isFloated = ['left', 'right'].includes(readString(block.props?.float) ?? '');
      return {
        width: maxWidth === undefined ? '100%' : `min(100%, ${maxWidth}px)`,
        ...(!isFloated && { maxWidth: '100%' }),
      };
    }

    const width = readPositiveNumber(block.props?.width);
    if (width !== undefined) return { width: `min(100%, ${width}px)` };
    return undefined;
  }

  const width = readPositiveNumber(block.props?.width);
  if (width === undefined) return undefined;

  return { width: `min(100%, ${width}px)` };
}

export function shouldForceFullWidthBlock(block: Pick<PageBlock, 'blockType'>): boolean {
  return (
    block.blockType === 'video' ||
    block.blockType === 'embed' ||
    block.blockType === 'raw_html' ||
    block.blockType === 'form' ||
    block.blockType === 'collection' ||
    block.blockType === 'index_collection'
  );
}

// A shrink-to-fit (w-auto) group can't reliably size itself around a force-full-width
// descendant (form/embed/video/...): those blocks have no stable intrinsic width of their own
// (e.g. a bare iframe or text input falls back to the browser's ~300px replaced-element
// default), so the group's computed width ends up arbitrary instead of filling its container.
export function blockOrDescendantsForceFullWidth(
  block: PageBlock,
  getChildren: (block: PageBlock) => PageBlock[],
): boolean {
  if (shouldForceFullWidthBlock(block)) {
    return true;
  }

  return getChildren(block).some((child) => blockOrDescendantsForceFullWidth(child, getChildren));
}
